import { ViewSuperblocksV2Provider } from "contexts/ViewSuperblocksV2Context";
import { memo, Suspense, lazy, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Navigate,
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useRouteError,
  createMemoryRouter,
  redirect,
} from "react-router-dom";

import {
  AccessMode,
  IntegrationKind,
  isEnterpriseLikePlan,
  OAUTH_CALLBACK_PATH,
  OrgFeature,
} from "@superblocksteam/shared";

import Auth0ProviderWithHistory from "auth/authProvider";
import AuthWrapper from "auth/authWrapper";
import { LoginComponent, LogoutComponent, SignupComponent } from "auth/utils";
import { AiQuotaPaywallModal } from "components/AiQuotaPaywallModal";
import EmailVerificationWrapper from "components/app/EmailVerification/EmailVerificationWrapper";
import { IntercomOverlay } from "components/app/Intercom/IntercomOverlay";
import { FullPageSpinner } from "components/ui/FullPageSpinner";
import {
  VIEW_ACCESS_TOKENS,
  VIEW_AGENTS,
  VIEW_GROUPS,
  VIEW_OBSERVABILITY,
  VIEW_PROFILES,
  VIEW_REPOSITORIES,
  VIEW_ROLES,
  VIEW_SECRET_STORES,
  VIEW_USERS,
  VIEW_GROUP_MEMBERS,
  MANAGE_AI_INFERENCE,
  MANAGE_GROUPS,
  MANAGE_ORGANIZATION,
  MANAGE_POLICY_AGENTS,
  READ_ORGANIZATION,
  MANAGE_BILLING,
  VIEW_POLICY_AGENTS,
} from "constants/rbac";
import CreditCardGateWrapper from "core/components/CreditCardGate/CreditCardGateWrapper";
import JoinOrgGateWrapper from "core/components/JoinOrgGate/JoinOrgGateWrapper";
import { JwtContextProvider } from "hooks/ui/JwtProvider";
import { useFeatureFlag } from "hooks/ui/useFeatureFlag";
import { useOrgFeature } from "hooks/ui/useOrgFeature";
import { closeAiQuotaPaywallModal } from "legacy/actions/modalActions";
import { ApplicationRoute } from "legacy/constants/routes";
import ErrorPage from "legacy/pages/common/ErrorPage";
import PageLoadingBar from "legacy/pages/common/PageLoadingSpinner";
import PageNotFound from "legacy/pages/common/PageNotFound";
import {
  getSafeCrash,
  getSafeCrashCode,
  getSafeCrashReason,
} from "legacy/selectors/errorSelectors";
import { getModalState } from "legacy/selectors/modalSelectors";
import { getAccessMode } from "legacy/selectors/usersSelectors";
import { AuthorizedPageWrapper } from "pages/AccessTokens/AuthorizedPageWrapper";
import OAuthImplicitCallback from "pages/components/OAuthCallback";
import { RoleAndPermissionsPages } from "pages/Permissions/constants";
import { POLICY_GATES_DEMO_URL, ROLES_AND_PERMISSIONS_URL } from "pages/routes";
import { useAppDispatch, useAppSelector } from "store/helpers";
import { Flag } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations";
import { useTracingManager } from "tracing/hooks";
import { isDev } from "utils/env";

import { ApiProfileReloader } from "./ApiProfileReloader";
import { SuperuserPageWrapper } from "./pages/AccessTokens/SuperUserPageWrapper";
import { AdminDemoProvider } from "./pages/AdminDemo/AdminDemoProvider";

const loadingIndicator = <PageLoadingBar />;

const LazyDowngradeModal = lazy(
  () => import(/* webpackChunkName: "modals" */ "pages/Home/DowngradeModal"),
);
const LazyPricingModal = lazy(
  () => import(/* webpackChunkName: "modals" */ "pages/Home/PricingModal"),
);
const LazyPurchasedModal = lazy(
  () => import(/* webpackChunkName: "modals" */ "pages/Home/PurchasedModal"),
);
const LazyQualificationModal = lazy(
  () =>
    import(/* webpackChunkName: "modals" */ "pages/Home/QualificationModal"),
);

function OrgFeatureGuard({
  feature,
  children,
  fallback,
}: {
  feature: OrgFeature;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const hasFeature = useOrgFeature(feature);
  return hasFeature ? <>{children}</> : <>{fallback ?? <PageNotFound />}</>;
}

function FeatureFlagGuard({
  flag,
  orFlag,
  children,
}: {
  flag: Flag;
  orFlag?: Flag;
  children: React.ReactNode;
}) {
  const enabled = useFeatureFlag(flag);
  const orEnabled = useFeatureFlag(orFlag ?? flag);
  return enabled || orEnabled ? <>{children}</> : <PageNotFound />;
}

// Hybrid gate for the Secrets Management page. V1 orgs see it via the legacy
// org-feature gate; V2 orgs see it when the LD flag is on for the org so they
// can manage integration-level secrets via AWS Secrets Manager (APPS-3877).
function SecretsManagementGuard({ children }: { children: React.ReactNode }) {
  const hasLegacy = useOrgFeature(OrgFeature.LegacySecretsManagement);
  const v2Enabled = useFeatureFlag(Flag.SECRETS_MANAGEMENT_V2_ENABLED);
  return hasLegacy || v2Enabled ? <>{children}</> : <PageNotFound />;
}

function EnterpriseOnlyRouteGuard({ children }: { children: React.ReactNode }) {
  const organization = useSelector(selectOnlyOrganization);
  const canAccess = isEnterpriseLikePlan(organization?.billing?.plan);

  return canAccess ? <>{children}</> : <Navigate to="/home" replace />;
}

function PrivateRoutes() {
  return (
    <AuthWrapper>
      <ViewSuperblocksV2Provider>
        <JwtContextProvider>
          <EmailVerificationWrapper>
            <JoinOrgGateWrapper>
              <CreditCardGateWrapper>
                <Outlet />
              </CreditCardGateWrapper>
            </JoinOrgGateWrapper>
          </EmailVerificationWrapper>
        </JwtContextProvider>
      </ViewSuperblocksV2Provider>
    </AuthWrapper>
  );
}

const MemoizedPrivateRoutes = memo(PrivateRoutes);

async function lazyHome() {
  const { default: Home } = await import(
    /* webpackChunkName: "home" */ "pages/Home"
  );
  return {
    element: (
      <IntercomOverlay>
        <Home />
      </IntercomOverlay>
    ),
  };
}

async function lazyCreateFromPrompt() {
  const { default: CreateFromPrompt } = await import(
    /* webpackChunkName: "create-from-prompt" */ "pages/CreateFromPrompt"
  );
  return { element: <CreateFromPrompt /> };
}

async function lazyIntegrations() {
  const { default: Integrations } = await import(
    /* webpackChunkName: "integrations" */ "pages/Integrations"
  );
  return {
    element: (
      <IntercomOverlay>
        <Integrations />
      </IntercomOverlay>
    ),
  };
}

async function lazyIntegrationForm(kind: IntegrationKind) {
  const { IntegrationForm } = await import(
    /* webpackChunkName: "integration-form" */ "pages/Integrations/IntegrationForm"
  );
  return {
    element: (
      <IntercomOverlay>
        <IntegrationForm kind={kind} />
      </IntercomOverlay>
    ),
  };
}

async function lazyProfiles() {
  const { default: Profiles } = await import(
    /* webpackChunkName: "profiles" */ "pages/Profiles"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_PROFILES]}>
          <Profiles />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyEnvironments() {
  const { default: Environments } = await import(
    /* webpackChunkName: "environments" */ "pages/Environments/Environments"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_PROFILES]}>
          <Environments />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyRepositories() {
  const { default: Repositories } = await import(
    /* webpackChunkName: "repositories" */ "pages/Repositories"
  );
  return {
    element: (
      <OrgFeatureGuard feature={OrgFeature.LegacySourceControl}>
        <IntercomOverlay>
          <AuthorizedPageWrapper permissions={[VIEW_REPOSITORIES]}>
            <Repositories />
          </AuthorizedPageWrapper>
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazyRepositoryDetails() {
  const { default: RepositoryDetails } = await import(
    /* webpackChunkName: "repository-details" */ "pages/Repositories/RepositoryDetails"
  );
  return {
    element: (
      <OrgFeatureGuard feature={OrgFeature.LegacySourceControl}>
        <IntercomOverlay>
          <RepositoryDetails />
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazyAgents() {
  const { default: Agents } = await import(
    /* webpackChunkName: "agents" */ "pages/Agents"
  );
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuthorizedPageWrapper permissions={[VIEW_AGENTS]}>
            <Agents />
          </AuthorizedPageWrapper>
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAgentDeploymentInstructionPage() {
  const { default: AgentDeploymentInstructionPage } = await import(
    /* webpackChunkName: "agent-deployment" */ "pages/Agents/AgentDeploymentInstructionPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuthorizedPageWrapper permissions={[VIEW_AGENTS]}>
            <AgentDeploymentInstructionPage />
          </AuthorizedPageWrapper>
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAccessTokensPage() {
  const { default: AccessTokensPage } = await import(
    /* webpackChunkName: "access-tokens" */ "pages/AccessTokens"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_ACCESS_TOKENS]}>
          <AccessTokensPage />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

let knowledgeComponents: typeof import("pages/ClarkKnowledge/routes") | null =
  null;

async function loadKnowledgeComponents() {
  if (!knowledgeComponents) {
    knowledgeComponents = await import(
      /* webpackChunkName: "clark-knowledge" */ "pages/ClarkKnowledge/routes"
    );
  }
  return knowledgeComponents;
}

async function lazyKnowledgeLayout() {
  const { KnowledgeLayout } = await loadKnowledgeComponents();
  return {
    element: (
      <OrgFeatureGuard
        feature={OrgFeature.Knowledge}
        fallback={<AccessModeRedirect />}
      >
        <IntercomOverlay>
          <KnowledgeLayout />
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazyKnowledgeList() {
  const { KnowledgeListPage } = await loadKnowledgeComponents();
  return { element: <KnowledgeListPage /> };
}

async function lazyKnowledgeCreate() {
  const { KnowledgeCreate } = await loadKnowledgeComponents();
  return { element: <KnowledgeCreate /> };
}

async function lazyKnowledgeDetail() {
  const { KnowledgeDetail } = await loadKnowledgeComponents();
  return { element: <KnowledgeDetail /> };
}

async function lazyDatabasePage() {
  const { DatabasePage } = await import(
    /* webpackChunkName: "database" */ "pages/Database/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <DatabasePage />
      </IntercomOverlay>
    ),
  };
}

async function lazyExternalAuthPage() {
  const { ExternalAuthPage } = await import(
    /* webpackChunkName: "external-auth" */ "pages/ExternalAuth/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.NAV_REDESIGN_ENABLED}>
          <ExternalAuthPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyExternalExperiencePage() {
  const { ExternalExperiencePage } = await import(
    /* webpackChunkName: "external-experience" */ "pages/ExternalExperience/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.NAV_REDESIGN_ENABLED}>
          <ExternalExperiencePage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyExternalUsersPage() {
  const { ExternalUsersPage } = await import(
    /* webpackChunkName: "external-users" */ "pages/ExternalUsers/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.NAV_REDESIGN_ENABLED}>
          <ExternalUsersPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyFileStoragePage() {
  const { FileStoragePage } = await import(
    /* webpackChunkName: "file-storage" */ "pages/FileStorage/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <FileStoragePage />
      </IntercomOverlay>
    ),
  };
}

async function lazyAppAnalyticsPage() {
  const { AppAnalyticsPage } = await import(
    /* webpackChunkName: "app-analytics" */ "pages/AppAnalytics/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AppAnalyticsPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyObservabilityMonitorPage() {
  const { ObservabilityMonitorPage } = await import(
    /* webpackChunkName: "observability-monitor" */ "pages/ObservabilityMonitor/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <ObservabilityMonitorPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyAgentsPage() {
  const { default: PolicyGatesDemo } = await import(
    /* webpackChunkName: "policy-gates-demo" */ "pages/PolicyGatesDemo"
  );
  return {
    element: (
      <IntercomOverlay>
        <PolicyGatesDemo />
      </IntercomOverlay>
    ),
  };
}

async function lazyPlatformMcpPage() {
  const { PlatformMcpPage } = await import(
    /* webpackChunkName: "platform-mcp" */ "pages/PlatformMcp/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <PlatformMcpPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyOverviewPage() {
  const { OverviewPage } = await import(
    /* webpackChunkName: "overview" */ "pages/Overview/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <OverviewPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyPlatformSetupPage() {
  const { PlatformSetupPage } = await import(
    /* webpackChunkName: "platform-setup" */ "pages/PlatformSetup/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <PlatformSetupPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyIdentityPage() {
  const { IdentityPage } = await import(
    /* webpackChunkName: "identity" */ "pages/Identity/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <IdentityPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyWorkflowsPage() {
  const { WorkflowsPage } = await import(
    /* webpackChunkName: "workflows" */ "pages/Workflows/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <WorkflowsPage />
      </IntercomOverlay>
    ),
  };
}

async function lazyUsersPage() {
  const { default: UsersPage } = await import(
    /* webpackChunkName: "users" */ "pages/Users"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_USERS]}>
          <UsersPage />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyUserDetail() {
  const { default: UserDetail } = await import(
    /* webpackChunkName: "user-detail" */ "pages/Users/UserDetail"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_USERS]}>
          <UserDetail />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyGroups() {
  const { default: Groups } = await import(
    /* webpackChunkName: "groups" */ "pages/Groups"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_GROUPS]}>
          <Groups />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyGroupDetail() {
  const { default: GroupDetail } = await import(
    /* webpackChunkName: "group-detail" */ "pages/Groups/GroupDetail"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper
          permissions={[VIEW_GROUP_MEMBERS, MANAGE_GROUPS]}
        >
          <GroupDetail />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyRolesAndPermissions() {
  const { default: RolesAndPermissions } = await import(
    /* webpackChunkName: "roles-permissions" */ "pages/Permissions/RolesAndPermissions"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_ROLES]}>
          <RolesAndPermissions />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyAuditLogs() {
  const { default: AuditLogs } = await import(
    /* webpackChunkName: "audit-logs" */ "pages/AuditLogs"
  );
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuditLogs />
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyObservability() {
  const { default: Observability } = await import(
    /* webpackChunkName: "observability" */ "pages/Observability"
  );
  return {
    element: (
      <OrgFeatureGuard feature={OrgFeature.LegacyObservability}>
        <IntercomOverlay>
          <AuthorizedPageWrapper permissions={[VIEW_OBSERVABILITY]}>
            <Observability />
          </AuthorizedPageWrapper>
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazySecretsManagement() {
  const { default: SecretsManagement } = await import(
    /* webpackChunkName: "secrets-management" */ "pages/SecretsManagement"
  );
  return {
    element: (
      <SecretsManagementGuard>
        <IntercomOverlay>
          <AuthorizedPageWrapper permissions={[VIEW_SECRET_STORES]}>
            <SecretsManagement />
          </AuthorizedPageWrapper>
        </IntercomOverlay>
      </SecretsManagementGuard>
    ),
  };
}

async function lazyGeneralPage() {
  const { default: GeneralPage } = await import(
    /* webpackChunkName: "general" */ "pages/General"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[READ_ORGANIZATION]}>
          <GeneralPage />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyUsagePage() {
  const { default: UsagePage } = await import(
    /* webpackChunkName: "usage" */ "pages/Usage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.BILLING_USAGE_PAGE_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_BILLING]}>
            <UsagePage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAiInferencePage() {
  const { default: AiInferencePage } = await import(
    /* webpackChunkName: "ai-inference" */ "pages/AiInference"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.AI_BYO_INFERENCE_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_AI_INFERENCE]}>
            <AiInferencePage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

// Per APPS-4229: per-org Package Registry settings page. Gated on
// `superblocks.npm-registry.enabled` (per-org LD flag from APPS-4228) and
// on org.manage so non-admin members can't even open the form. Mounted
// under `/package-registry` to keep the URL stable across cycles where the
// `npm-registry` server route owns the API path.
async function lazyPackageRegistryPage() {
  const { default: PackageRegistryPage } = await import(
    /* webpackChunkName: "package-registry" */ "pages/PackageRegistry"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.NPM_REGISTRY_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_ORGANIZATION]}>
            <PackageRegistryPage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazySpendingPage() {
  const { default: SpendingPage } = await import(
    /* webpackChunkName: "spending" */ "pages/Spending"
  );
  return {
    element: (
      <IntercomOverlay>
        {/* Gated on its own flag (not BILLING_USAGE_PAGE_ENABLED) so ops can
            roll out /spending independently of /usage. */}
        <FeatureFlagGuard
          flag={Flag.BILLING_SPENDING_PAGE_ENABLED}
          orFlag={Flag.NAV_REDESIGN_ENABLED}
        >
          <SpendingPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPersonalSettings() {
  const { default: PersonalSettings } = await import(
    /* webpackChunkName: "personal-settings" */ "pages/PersonalSettings"
  );
  return {
    element: (
      <IntercomOverlay>
        <PersonalSettings />
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGatesDemo() {
  const { default: PolicyGatesDemo } = await import(
    /* webpackChunkName: "policy-gates-demo" */ "pages/PolicyGatesDemo"
  );
  return {
    element: (
      <IntercomOverlay>
        <PolicyGatesDemo />
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGatesDemoReset() {
  const { resetPolicyGatesDemoStorage } = await import(
    /* webpackChunkName: "policy-gates-demo" */ "pages/PolicyGatesDemo/policyGateCreatedGateStore"
  );
  return {
    loader: () => {
      resetPolicyGatesDemoStorage();
      window.location.replace(POLICY_GATES_DEMO_URL);
      return redirect(POLICY_GATES_DEMO_URL);
    },
  };
}

async function lazyAppAiPage() {
  const { default: AppAiPage } = await import(
    /* webpackChunkName: "app-ai" */ "pages/AppAi"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard
          flag={Flag.APP_AI_DEFAULT_INTEGRATION_ENABLED}
          orFlag={Flag.NAV_REDESIGN_ENABLED}
        >
          <AppAiPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGatesPage() {
  const { default: PolicyGatesPage } = await import(
    /* webpackChunkName: "policy-gates" */ "pages/PolicyGates"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.POLICY_GATES_MVP_ENABLED}>
          <AuthorizedPageWrapper permissions={[VIEW_POLICY_AGENTS]}>
            <PolicyGatesPage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazySecurityAgentCreatePage() {
  const { default: SecurityAgentCreatePage } = await import(
    /* webpackChunkName: "policy-gates" */ "pages/PolicyGates/SecurityAgentCreatePage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.POLICY_GATES_MVP_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_POLICY_AGENTS]}>
            <SecurityAgentCreatePage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGateRunsPage() {
  const { default: PolicyGateRunsPage } = await import(
    /* webpackChunkName: "policy-gates" */ "pages/PolicyGates/PolicyGateRunsPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.POLICY_GATES_MVP_ENABLED}>
          <AuthorizedPageWrapper permissions={[VIEW_POLICY_AGENTS]}>
            <PolicyGateRunsPage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGateRunReportPage() {
  const { default: PolicyGateRunReportPage } = await import(
    /* webpackChunkName: "policy-gates" */ "pages/PolicyGates/PolicyGateRunReportPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.POLICY_GATES_MVP_ENABLED}>
          <AuthorizedPageWrapper permissions={[VIEW_POLICY_AGENTS]}>
            <PolicyGateRunReportPage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyAgentEditPage() {
  const { default: PolicyGateRunsPage } = await import(
    /* webpackChunkName: "policy-gates" */ "pages/PolicyGates/PolicyGateRunsPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.POLICY_GATES_MVP_ENABLED}>
          <AuthorizedPageWrapper permissions={[VIEW_POLICY_AGENTS]}>
            <AuthorizedPageWrapper permissions={[MANAGE_POLICY_AGENTS]}>
              <PolicyGateRunsPage />
            </AuthorizedPageWrapper>
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyCheckoutLoader() {
  const { default: CheckoutLoader } = await import(
    /* webpackChunkName: "checkout" */ "pages/Checkout/loader"
  );
  return {
    element: (
      <IntercomOverlay>
        <CheckoutLoader />
      </IntercomOverlay>
    ),
  };
}

async function lazyAcceptInvite() {
  const { default: AcceptInvitePage } = await import(
    /* webpackChunkName: "accept-invite" */ "pages/AcceptInvite"
  );
  return { element: <AcceptInvitePage /> };
}

async function lazyCheckoutCompletePage() {
  const { default: CheckoutCompletePage } = await import(
    /* webpackChunkName: "checkout-complete" */ "pages/CheckoutComplete"
  );
  return {
    element: (
      <IntercomOverlay>
        <CheckoutCompletePage />
      </IntercomOverlay>
    ),
  };
}

async function lazyDBFSDownloadPage() {
  const { default: DBFSDownloadPage } = await import(
    /* webpackChunkName: "dbfs-download" */ "pages/DBFSDownload"
  );
  return {
    element: (
      <SuperuserPageWrapper>
        <DBFSDownloadPage />
      </SuperuserPageWrapper>
    ),
  };
}

async function lazyGitHubOAuthCallback() {
  const { default: GitHubOAuthCallback } = await import(
    /* webpackChunkName: "github-oauth" */ "pages/components/GithubOAuthCallback"
  );
  return { element: <GitHubOAuthCallback /> };
}

function AccessModeRedirect() {
  const accessMode = useSelector(getAccessMode);
  if (accessMode === AccessMode.VISITOR) {
    return <Navigate to={"/login"} />;
  }
  return <Navigate to={"/home"} />;
}

function HardRedirect() {
  const search = window.location.search;
  if (search.includes("code=") && search.includes("state=")) {
    return null;
  }
  window.location.replace(window.location.href);
  return null;
}

function GlobalAiQuotaPaywallModal() {
  const modalState = useAppSelector(getModalState);
  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    dispatch(closeAiQuotaPaywallModal());
  }, [dispatch]);

  return (
    <AiQuotaPaywallModal
      open={modalState.aiQuotaPaywallReason !== null}
      reason={modalState.aiQuotaPaywallReason ?? undefined}
      onClose={onClose}
    />
  );
}

function TopLevelRoutes() {
  useTracingManager();

  const safeCrash = useSelector(getSafeCrash);
  const safeCrashCode = useSelector(getSafeCrashCode);
  const safeCrashReason = useSelector(getSafeCrashReason);

  if (safeCrash && safeCrashCode) {
    return (
      <Auth0ProviderWithHistory>
        <ErrorPage code={safeCrashCode} reason={safeCrashReason} />
      </Auth0ProviderWithHistory>
    );
  }

  return (
    <Auth0ProviderWithHistory>
      <Outlet />

      {/* These can appear on any page */}
      <LazyDowngradeModal />
      <LazyPricingModal />
      <GlobalAiQuotaPaywallModal />
      <LazyPurchasedModal />
      <LazyQualificationModal />

      {/* TODO: Remove this once control flow is fully released */}
      <ApiProfileReloader />
    </Auth0ProviderWithHistory>
  );
}

// See https://github.com/remix-run/react-router/discussions/10166
const BubbleError = () => {
  const error = useRouteError();
  throw error;
};

// detect if running in test environment because router depends on some browser apis
export const router = (
  process.env.NODE_ENV === "test" ? createMemoryRouter : createBrowserRouter
)([
  {
    element: <TopLevelRoutes />,
    errorElement: <BubbleError />,
    children: [
      { path: "/login", element: <LoginComponent /> },
      { path: "/signup", element: <SignupComponent /> },
      { path: "/logout", element: <LogoutComponent /> },
      ...(isDev()
        ? [
            {
              path: "/embed/applications/preview/*",
              element: <HardRedirect />,
            },
            {
              path: "/embed/applications/*",
              element: <HardRedirect />,
            },
          ]
        : []),
      {
        element: (
          <AdminDemoProvider>
            <MemoizedPrivateRoutes />
          </AdminDemoProvider>
        ),
        children: [
          {
            path: "create",
            lazy: lazyCreateFromPrompt,
          },
          {
            path: "folders/:folderId/*",
            lazy: lazyHome,
          },
          {
            path: "integrations",
            lazy: lazyIntegrations,
          },
          {
            path: "profiles",
            lazy: lazyProfiles,
          },
          {
            path: "environments",
            lazy: lazyEnvironments,
          },
          {
            path: "repositories",
            lazy: lazyRepositories,
          },
          {
            path: "repositories/:repositoryId",
            lazy: lazyRepositoryDetails,
          },
          {
            path: ApplicationRoute.Base,
            element: <HardRedirect />,
          },
          {
            path: "home?/*",
            lazy: lazyHome,
          },
          {
            path: "apps/*",
            lazy: lazyHome,
          },
          {
            path: "opas",
            lazy: lazyAgents,
          },
          {
            path: "opas/instructions",
            lazy: lazyAgentDeploymentInstructionPage,
          },
          {
            path: "access-tokens",
            lazy: lazyAccessTokensPage,
          },
          {
            path: "database",
            lazy: lazyDatabasePage,
          },
          {
            path: "external-auth",
            lazy: lazyExternalAuthPage,
          },
          {
            path: "external-experience",
            lazy: lazyExternalExperiencePage,
          },
          {
            path: "external-users",
            lazy: lazyExternalUsersPage,
          },
          {
            path: "overview",
            lazy: lazyOverviewPage,
          },
          {
            path: "platform-setup",
            lazy: lazyPlatformSetupPage,
          },
          {
            path: "workflows",
            lazy: lazyWorkflowsPage,
          },
          {
            path: "file-storage",
            lazy: lazyFileStoragePage,
          },
          {
            path: "identity",
            lazy: lazyIdentityPage,
          },
          {
            path: "app-analytics",
            lazy: lazyAppAnalyticsPage,
          },
          {
            path: "observability-monitor",
            lazy: lazyObservabilityMonitorPage,
          },
          {
            path: "policy-agents",
            lazy: lazyPolicyAgentsPage,
          },
          {
            path: "platform-mcp",
            lazy: lazyPlatformMcpPage,
          },
          {
            path: "knowledge",
            lazy: lazyKnowledgeLayout,
            children: [
              {
                index: true,
                lazy: lazyKnowledgeList,
              },
              {
                path: "new",
                lazy: lazyKnowledgeCreate,
              },
              {
                path: ":factId",
                lazy: lazyKnowledgeDetail,
              },
            ],
          },
          {
            path: "users",
            lazy: lazyUsersPage,
          },
          {
            path: "users/:userId/:pageType",
            lazy: lazyUserDetail,
          },
          {
            path: "groups",
            lazy: lazyGroups,
          },
          {
            path: "groups/:groupId/:pageType",
            lazy: lazyGroupDetail,
          },
          {
            path: "roles-and-permissions",
            element: (
              <Navigate
                to={ROLES_AND_PERMISSIONS_URL(
                  RoleAndPermissionsPages.ORGANIZATION_ROLES,
                )}
                replace
              />
            ),
          },
          {
            path: ROLES_AND_PERMISSIONS_URL(),
            lazy: lazyRolesAndPermissions,
          },
          {
            path: "audit",
            lazy: lazyAuditLogs,
          },
          {
            path: "integrations/:pluginId/:datasourceId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.PLUGIN),
          },
          {
            path: "integrations/:pluginId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.PLUGIN),
          },
          {
            path: "workflows/:apiId/action?/:actionId?/*",
            element: <HardRedirect />,
          },
          {
            path: "workflows",
            element: <AccessModeRedirect />,
          },
          {
            path: "scheduled_jobs/:apiId/action?/:actionId?/*",
            element: <HardRedirect />,
          },
          {
            path: "scheduled_jobs",
            element: <AccessModeRedirect />,
          },
          {
            path: "oauth/github-callback",
            lazy: lazyGitHubOAuthCallback,
          },
          {
            path: "observability/:pageType?",
            lazy: lazyObservability,
          },
          {
            path: "secrets-management",
            lazy: lazySecretsManagement,
          },
          {
            path: "secrets-management/:pluginId/:datasourceId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.SECRET),
          },
          {
            path: "secrets-management/:pluginId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.SECRET),
          },
          {
            path: "general",
            lazy: lazyGeneralPage,
          },
          {
            path: "usage",
            lazy: lazyUsagePage,
          },
          {
            path: "ai-inference",
            lazy: lazyAiInferencePage,
          },
          {
            path: "package-registry",
            lazy: lazyPackageRegistryPage,
          },
          {
            path: "spending",
            lazy: lazySpendingPage,
          },
          {
            path: "personal-settings",
            lazy: lazyPersonalSettings,
          },
          {
            path: "settings/policy-gates",
            lazy: lazyPolicyGatesDemo,
          },
          {
            path: "settings/policy-gates/reset",
            lazy: lazyPolicyGatesDemoReset,
          },
          {
            path: "app-ai",
            lazy: lazyAppAiPage,
          },
          {
            path: "policy-gates",
            lazy: lazyPolicyGatesPage,
          },
          {
            path: "policy-gates/new",
            lazy: lazySecurityAgentCreatePage,
          },
          {
            path: "policy-gates/:policyId/runs/:runId",
            lazy: lazyPolicyGateRunReportPage,
          },
          {
            path: "policy-gates/:policyId",
            lazy: lazyPolicyGateRunsPage,
          },
          {
            path: "policy-gates/:policyId/edit",
            lazy: lazyPolicyAgentEditPage,
          },
          {
            path: "accept-invite",
            lazy: lazyAcceptInvite,
          },
          {
            path: "checkout",
            lazy: lazyCheckoutLoader,
          },
          {
            path: "checkout/complete",
            lazy: lazyCheckoutCompletePage,
          },
          {
            path: "admin/dbfs",
            lazy: lazyDBFSDownloadPage,
          },
          {
            path: OAUTH_CALLBACK_PATH,
            element: <OAuthImplicitCallback />,
          },
        ],
      },
      {
        path: "*",
        element: <PageNotFound />,
      },
    ],
  },
]);

const Router = () => {
  return (
    <Suspense fallback={loadingIndicator}>
      <RouterProvider router={router} fallbackElement={<FullPageSpinner />} />
    </Suspense>
  );
};

// Uses basic memoization because routes can't change
export default memo(Router);
